import { ControlsIcon, DeleteIcon, EyeIcon, EyeOffIcon } from '@assets/icons';
import { IconButton } from '@components/button';
import { Checkbox, ColorPicker, Select, Slider } from '@components/form';
import { Group } from '@components/layout';
import { Typography } from '@components/typography';
import { Box, Menu, MenuItem, Stack, useTheme } from '@mui/material';
import { useState } from 'react';
import { useImageOverlayContext } from './DatasetsDetailsImagesSliderOverlayContext';

type InferenceSettingsRowProps = {
  index: number;
};

export const InferenceSettingsRow = ({ index }: InferenceSettingsRowProps) => {
  const { typography } = useTheme();
  const { datasetInferences, inferenceSettings, removeInferenceLayer, updateInferenceSetting } =
    useImageOverlayContext();

  const [optionsMenuAnchorRef, setOptionsMenuAnchorRef] = useState<HTMLElement | null>(null);

  return (
    <Group alignItems="center" gap={4} py={1} maxWidth="md">
      <Select
        onChange={(e) => {
          updateInferenceSetting(index, 'inferenceId', e.target.value as string);
        }}
        value={inferenceSettings[index].inferenceId || ''}
        fullWidth={false}
        sx={{ minWidth: '200px' }}
      >
        {datasetInferences.map((i) => (
          <MenuItem key={i.inferenceId} value={i.inferenceId}>
            {i.model}
          </MenuItem>
        ))}
      </Select>
      <Box flex={1} />
      <ColorPicker
        color={inferenceSettings[index].color}
        onChange={(newColor) => updateInferenceSetting(index, 'color', newColor)}
      />
      <IconButton
        Icon={ControlsIcon}
        size={24}
        onClick={(e) => setOptionsMenuAnchorRef(e.currentTarget)}
      />
      <Menu
        open={Boolean(optionsMenuAnchorRef)}
        anchorEl={optionsMenuAnchorRef}
        onClose={() => setOptionsMenuAnchorRef(null)}
        sx={{ '& .MuiList-root': { py: 3, px: 4 } }}
      >
        <Stack gap={1}>
          <Checkbox
            label="Show inference labels"
            checked={inferenceSettings[index]?.showLabels}
            onClick={() =>
              updateInferenceSetting(index, 'showLabels', !inferenceSettings[index]?.showLabels)
            }
            sx={{ '& .MuiTypography-root': { ...typography.caption2 } }}
          />
          <Stack>
            <Group justifyContent="space-between" gap={2}>
              <Typography variant="caption2">Confidence threshold</Typography>
              <Typography variant="caption2">
                {inferenceSettings[index]?.confidenceThreshold}
              </Typography>
            </Group>
            <Slider
              min={0}
              max={100}
              step={1}
              value={inferenceSettings[index]?.confidenceThreshold}
              onChange={(_, newThreshold) =>
                updateInferenceSetting(index, 'confidenceThreshold', newThreshold as number)
              }
            />
          </Stack>
          <Stack>
            <Typography variant="caption2">Outline width</Typography>
            <Slider
              min={0.5}
              max={5}
              step={0.5}
              value={inferenceSettings[index]?.strokeWidth}
              onChange={(_, newWidth) =>
                updateInferenceSetting(index, 'strokeWidth', newWidth as number)
              }
            />
          </Stack>
        </Stack>
      </Menu>
      <IconButton
        Icon={inferenceSettings[index]?.visible ? EyeIcon : EyeOffIcon}
        size={24}
        onClick={() => updateInferenceSetting(index, 'visible', !inferenceSettings[index]?.visible)}
      />
      <IconButton Icon={DeleteIcon} size={24} onClick={() => removeInferenceLayer(index)} />
    </Group>
  );
};
